import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { CircleInfoIcon, HandKeyIcon, PostpaidTabIcon, PrepaidTabIcon, UserKeyIcon } from "../../../../../assets/icons";
import { HelpModal } from "../../../../../common/components/help-modal/HelpModal";
import { IconRadioGroup, IconRadioOption } from "../../../../../common/components/icon-radio-group/IconRadioGroup";
import { IconTabs } from "../../../../../common/components/icon-tabs/IconTabs";
import { VerifyMeterNumberField, VerifyMeterNumberFieldHandle } from "../../../../../common/components/verify-meter-number/VerifyMeterNumberField";
import { ConsumerType } from "../../../../../model/energy-profile";
import { AddNewMeterNumberRequest, SaveAsDraftMeterRequest } from "../../../../../model/vending/vending-interfaces";
import { useAddMeterNumberToEnergyProfileMutation, useSaveCustomerMeterAsDraftMutation } from "../../../../../service/vending-service";
import { RootState } from "../../../../../stores/store";
import { ApplicationRoutes } from "../../../../../routes";
import CustomerPageLayout from "../../../customer-page-layout/CustomerPageLayout";
import { EnergyProfileNinChoice } from "./EnergyProfileNinChoice";

enum AddMeterStep {
	MeterVerification = "meterVerification",
	ResidencySelection = "residencySelection",
	NinChoice = "ninChoice",
}

export const AddMeterPage = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const storedProfile = useSelector((state: RootState) => state.customerEnergyProfile);

	const [selectedTab, setSelectedTab] = useState<ConsumerType | null>(null);
	const [meterNumber, setMeterNumber] = useState<string>("");
	const [currentStep, setCurrentStep] = useState<AddMeterStep>(AddMeterStep.MeterVerification);
	const [hasVerificationFailed, setHasVerificationFailed] = useState(false);
	const [selectedResidencyIndex, setSelectedResidencyIndex] = useState<number | null>(null);
	const [infoModalOpen, setInfoModalOpen] = useState(false);
	const [isSaving, setIsSaving] = useState(false);

	const prepaidMeterRef = useRef<VerifyMeterNumberFieldHandle>(null);
	const postpaidMeterRef = useRef<VerifyMeterNumberFieldHandle>(null);

	const [addMeterNumberToEnergyProfile] = useAddMeterNumberToEnergyProfileMutation();
	const [saveCustomerMeterAsDraft] = useSaveCustomerMeterAsDraftMutation();

	const utilityId = process.env.REACT_APP_UTILITY_ID || storedProfile.utilityId || "";

	const residencyOptions: IconRadioOption[] = [
		{ title: t("common.labels.tenant"), subtitle: t("energyManagement.energyProfile.tenantResidencyDescription"), icon: <UserKeyIcon /> },
		{ title: t("common.labels.landlord"), subtitle: t("energyManagement.energyProfile.landlordResidencyDescription"), icon: <HandKeyIcon /> },
	];

	const getSelectedTabIndex = (): number | null => {
		if (selectedTab === ConsumerType.Prepaid) return 0;
		if (selectedTab === ConsumerType.Postpaid) return 1;
		return null;
	};

	const changeTab = (type: ConsumerType) => {
		if (type === selectedTab) return;
		setSelectedTab(type);
		setMeterNumber("");
		setCurrentStep(AddMeterStep.MeterVerification);
		setHasVerificationFailed(false);
		setSelectedResidencyIndex(null);
	};

	const handleVerificationResult = (result: unknown) => {
		if (result) {
			setHasVerificationFailed(false);
			setCurrentStep(AddMeterStep.ResidencySelection);
		} else {
			if (currentStep === AddMeterStep.ResidencySelection) {
				setCurrentStep(AddMeterStep.MeterVerification);
				setSelectedResidencyIndex(null);
			}
			setHasVerificationFailed(meterNumber.length > 0);
		}
	};

	const residencyStatusStr = selectedResidencyIndex === 0 ? "Tenant" : selectedResidencyIndex === 1 ? "Landlord" : undefined;
	const consumerTypeStr = selectedTab != null ? ConsumerType[selectedTab] : undefined;

	const buildAddMeterRequest = (): AddNewMeterNumberRequest => ({
		meterNumber: selectedTab === ConsumerType.Prepaid ? meterNumber : undefined,
		accountNumber: selectedTab === ConsumerType.Postpaid ? meterNumber : undefined,
		consumerType: consumerTypeStr,
		residencyStatus: residencyStatusStr,
	});

	const goToSwitchMeter = () => navigate(`/${ApplicationRoutes.SETTINGS}/${ApplicationRoutes.CUSTOMER_ENERGY_PROFILE}`);

	const navigateToNin = () => {
		navigate(`/${ApplicationRoutes.SETTINGS}/${ApplicationRoutes.ENERGY_PROFILE_NIN}`, {
			state: {
				meterNumber,
				isLandlord: selectedResidencyIndex === 1,
				consumerType: selectedTab,
			},
		});
	};

	const saveAsDraft = async () => {
		if (!selectedTab || !meterNumber) return;

		setIsSaving(true);
		try {
			const request: SaveAsDraftMeterRequest = {
				meterNumber: selectedTab === ConsumerType.Prepaid ? meterNumber : undefined,
				accountNumber: selectedTab === ConsumerType.Postpaid ? meterNumber : undefined,
				consumerType: consumerTypeStr,
				residencyStatus: residencyStatusStr,
			};
			await saveCustomerMeterAsDraft(request).unwrap();
			toast.success(t("account.web.savedAsDraft"));
		} catch {
			toast.error(t("account.web.failedToSave"));
		} finally {
			setIsSaving(false);
		}
	};

	const submitForm = async () => {
		setIsSaving(true);
		try {
			await addMeterNumberToEnergyProfile(buildAddMeterRequest()).unwrap();
			if (!storedProfile.isNinVerified) {
				setCurrentStep(AddMeterStep.NinChoice);
			} else {
				toast.success(t("account.web.meterAddedSuccessfully"));
				goToSwitchMeter();
			}
		} catch {
			toast.error(t("account.web.failedToAddMeter"));
		} finally {
			setIsSaving(false);
		}
	};

	const triggerVerify = async () => {
		setIsSaving(true);
		const ref = selectedTab === ConsumerType.Prepaid ? prepaidMeterRef : postpaidMeterRef;
		await ref.current?.verify();
		setIsSaving(false);
	};

	const submitButtonPressed = async () => {
		if (currentStep === AddMeterStep.MeterVerification) {
			await triggerVerify();
		} else if (currentStep === AddMeterStep.ResidencySelection) {
			if (selectedResidencyIndex !== null) submitForm();
		}
	};

	if (currentStep === AddMeterStep.NinChoice) {
		return (
			<EnergyProfileNinChoice
				onSkip={() => {
					toast.success(t("account.web.meterAddedSuccessfully"));
					goToSwitchMeter();
				}}
				onVerify={navigateToNin}
			/>
		);
	}

	const isSubmitDisabled =
		isSaving ||
		!selectedTab ||
		(currentStep === AddMeterStep.MeterVerification && !meterNumber) ||
		(currentStep === AddMeterStep.ResidencySelection && selectedResidencyIndex === null);

	const showResidencySection = currentStep === AddMeterStep.ResidencySelection;

	return (
		<>
			<CustomerPageLayout title={t("common.labels.energyProfile")} showBackButton={true} contentMaxWidth={600} decreaseMainContentPadding>
				{/* Step 1: Select Service Type */}
				<IconTabs
					tabs={[
						{ title: t("common.labels.prepaid"), icon: <PrepaidTabIcon />, onTap: () => changeTab(ConsumerType.Prepaid) },
						{ title: t("common.labels.postpaid"), icon: <PostpaidTabIcon />, onTap: () => changeTab(ConsumerType.Postpaid) },
					]}
					selectedIndex={getSelectedTabIndex()}
					headerSectionTitle={t("energyManagement.energyProfile.selectServiceType")}
					headerSectionRightContent={
						<div onClick={() => setInfoModalOpen(true)} style={{ cursor: "pointer" }}>
							<CircleInfoIcon />
						</div>
					}
					iconSize={24}
					circleSize={40}
				/>

				<div style={{ height: "0.5rem" }} />

				{/* Prepaid content */}
				{selectedTab === ConsumerType.Prepaid && (
					<>
						<VerifyMeterNumberField
							ref={prepaidMeterRef}
							value={meterNumber}
							onChange={setMeterNumber}
							consumerType={ConsumerType.Prepaid}
							title={t("common.labels.meterNumber")}
							placeholder={t("common.labels.meterNumber")}
							utilityId={utilityId}
							onVerificationResult={handleVerificationResult}
							hideVerifyButton
							headerSectionRightContent={
								<div onClick={() => setInfoModalOpen(true)} style={{ cursor: "pointer" }}>
									<CircleInfoIcon />
								</div>
							}
						/>

						{showResidencySection && (
							<>
								<div style={{ height: "0.5rem" }} />
								<IconRadioGroup
									options={residencyOptions}
									selectedIndex={selectedResidencyIndex}
									onOptionSelected={setSelectedResidencyIndex}
									headerTitle={t("energyManagement.energyProfile.selectResidencyStatus")}
									headerRightContent={
										<div onClick={() => setInfoModalOpen(true)} style={{ cursor: "pointer" }}>
											<CircleInfoIcon />
										</div>
									}
									iconSize={24}
								/>
							</>
						)}
					</>
				)}

				{/* Postpaid content */}
				{selectedTab === ConsumerType.Postpaid && (
					<>
						<VerifyMeterNumberField
							ref={postpaidMeterRef}
							value={meterNumber}
							onChange={setMeterNumber}
							consumerType={ConsumerType.Postpaid}
							utilityId={utilityId}
							title={t("common.labels.accountNumber")}
							placeholder={t("common.labels.accountNumber")}
							onVerificationResult={handleVerificationResult}
							hideVerifyButton
						/>

						{showResidencySection && (
							<>
								<div style={{ height: "0.5rem" }} />
								<IconRadioGroup
									options={residencyOptions}
									selectedIndex={selectedResidencyIndex}
									onOptionSelected={setSelectedResidencyIndex}
									headerTitle={t("energyManagement.energyProfile.selectResidencyStatus")}
									headerRightContent={
										<div onClick={() => setInfoModalOpen(true)} style={{ cursor: "pointer" }}>
											<CircleInfoIcon />
										</div>
									}
									iconSize={24}
								/>
							</>
						)}
					</>
				)}

				<div style={{ height: "1rem" }} />

				{/* Bottom action buttons */}
				<div style={{ display: "flex", gap: "0.75rem", width: "100%" }}>
					{hasVerificationFailed && currentStep === AddMeterStep.MeterVerification ? (
						<>
							<button className="app-secondary-btn" onClick={saveAsDraft} disabled={isSaving} style={{ flex: 1, width: "auto" }}>
								{t("energyManagement.saveForLater")}
							</button>
							<button className="app-primary-btn" onClick={triggerVerify} disabled={isSaving} style={{ flex: 1, width: "auto" }}>
								{t("energyManagement.retry")}
							</button>
						</>
					) : (
						<button className="app-primary-btn" onClick={submitButtonPressed} disabled={isSubmitDisabled} style={{ flex: 1, width: "auto" }}>
							{t("common.actions.continueKey")}
						</button>
					)}
				</div>
			</CustomerPageLayout>

			<HelpModal
				isOpen={infoModalOpen}
				onClose={() => setInfoModalOpen(false)}
				title={t("common.labels.signinHelpTitle")}
				description={t("common.labels.signinHelpDescription")}
			/>
		</>
	);
};

export default AddMeterPage;
